import { Box, Card, Chip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { BookingService } from '../../Booking/Service';
import { formatDateTime } from '../../../utils/dateUtils';
import Loading from '../../../components/Loader/Loading';

const BookingHistory = ({ providerId }) => {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const res = await BookingService.bookingList({
          page: paginationModel.page,
          limit: paginationModel.pageSize,
          filters: {
            searchKey: '',
            sortKey: 'createdAt',
            sortDirection: 'desc',
            status: 'all',
            filterOptions: [{ fieldName: 'providerId', operator: 'equals', value: String(providerId) }]
          }
        });
        if (cancelled) return;
        if (res?.success == 1) {
          setRows(res.data?.list || []);
          setRowCount(res.data?.totalItems || 0);
        }
      } catch (e) {
        if (!cancelled) console.error('Failed to fetch bookings', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBookings();
    return () => { cancelled = true; };
  }, [paginationModel, providerId]);

  const columns = [
    {
      field: 'bookingNumber', headerName: 'Booking ID', flex: 1.5, headerAlign: 'center',
      renderCell: (params) => (
        <Typography
          sx={{ cursor: 'pointer', fontSize: '0.65rem', color: '#1976d2' }}
          onClick={() => window.open(`/admin/booking-details/${params.row.id}`, '_blank')}
        >
          #{params.row.bookingNumber}
        </Typography>
      ),
    },
    {
      field: 'serviceDate', headerName: 'Service Date', flex: 1.25, headerAlign: 'center',
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.6rem' }}>
          {params.row?.serviceDate ? formatDateTime(params.row.serviceDate, 'date') : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerName', headerName: 'Customer', width: 150, headerAlign: 'center',
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.65rem', textTransform: 'capitalize' }}>{params.value || 'N/A'}</Typography>
      ),
    },
    {
      field: 'bookingStatus', headerName: 'Status', flex: 1, headerAlign: 'center',
      renderCell: (params) => {
        const v = params.value;
        const colorMap = {
          requested: { color: '#E8832A', bg: '#FFEDD5' },
          rejected: { color: '#F80000', bg: '#f800001f' },
          completed: { color: '#009EB4', bg: '#009eb41a' },
          accepted: { color: '#40B962', bg: '#40b96236' },
          cancelled: { color: '#D81A23', bg: '#d81a231f' },
          ongoing: { color: '#1A44B8', bg: '#1a44b838' },
        };
        const style = colorMap[v] || { color: 'default', bg: 'default' };
        return (
          <Chip label={v} size="small" sx={{ fontWeight: 500, fontSize: 'x-small', color: style.color, bgcolor: style.bg }} />
        );
      },
    },
    {
      field: 'createdAt', headerName: 'Booked On', flex: 1, headerAlign: 'center',
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.6rem' }}>
          {params.row.createdAt ? formatDateTime(params.row.createdAt) : 'N/A'}
        </Typography>
      ),
    },
  ];

  return (
    <Card>
      <Box sx={{ p: '.5% 1%', border: '2px solid lightGray', bgcolor: 'lightGray' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Booking History</Typography>
      </Box>
      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ width: '100%', display: 'table', tableLayout: 'fixed', backgroundColor: '#fff', borderRadius: '.5rem' }}>
          {loading && <Loading />}
            <DataGrid
              rows={rows}
              columns={columns.map(c => ({ ...c, minWidth: c.minWidth || 150 }))}
              getRowId={(row) => row.id}
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              loading={loading}
              disableColumnMenu
              disableRowSelectionOnClick
              slots={{ noRowsOverlay: Loading, loadingOverlay: Loading }}
              autoHeight
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  border: 1, borderRight: 0, borderTop: 0, borderColor: 'lightgrey',
                  p: 1, textAlign: 'center', justifyContent: 'center', fontSize: '.75rem'
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '2px solid rgba(224, 224, 224, 1)',
                  borderTop: '2px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeader': {
                  borderLeft: '1px solid rgba(224, 224, 224, 1)',
                  '&:last-child': { borderRight: 'none' },
                },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#3e3e3e', fontSize: '.75rem' },
                '& .MuiDataGrid-row': {
                  '&:last-child .MuiDataGrid-cell': { borderBottom: '1px solid rgba(224, 224, 224, 1)' },
                  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                },
                '& .MuiDataGrid-columnSeparator': { display: 'none' },
              }}
            />
        </Box>
      </Box>
    </Card>
  );
};

export default BookingHistory;
